"use client"

import { PropsWithChildren, useEffect, useMemo, useState } from 'react'
import { Navbar } from '@/components/navbar'
import { Toaster } from '@/components/ui/sonner'
import ClientSelectorDialog from '@/components/dialogs/ClientSelectorDialog'
import { usePathname, useRouter } from 'next/navigation'
import { useTheme } from 'next-themes'
import { useMe } from '@/hooks/use-me'
import AuthShell from '@/components/layout/AuthShell'
import RequireTwoFactor from '@/components/security/RequireTwoFactor'
import { sessionCsrf } from '@/services/auth'
import { clientHomeRoute, isClientAllowedPath } from '@/utils/clientRoutes'

export default function ClientRoot({ children }: PropsWithChildren) {
  const pathname = usePathname()
  const { data: me, isLoading: isMeLoading } = useMe()
  const [noChrome, setNoChrome] = useState(false)
  const [persistedSection, setPersistedSection] = useState<string | null>(null)
  const isAuthPage = !!pathname && (
    pathname === '/sign-in' ||
    pathname === '/login' ||
    pathname === '/activate' ||
    pathname === '/reset-password' ||
    pathname.startsWith('/sign-in') ||
    pathname.startsWith('/login')
  )
  const isMarketingPage = pathname === '/'
  const isTwoFASetup = pathname === '/enable-2fa'
  const isAuthed = !!me

  useEffect(() => {
    try {
      const nextNoChrome = document.body.classList.contains('no-chrome')
      queueMicrotask(() => setNoChrome(nextNoChrome))
    } catch {
      queueMicrotask(() => setNoChrome(false))
    }
    try {
      const nextSection = window.localStorage.getItem('active.section')
      queueMicrotask(() => setPersistedSection(nextSection))
    } catch {
      queueMicrotask(() => setPersistedSection(null))
    }
  }, [])

  const isClientUser = !!me && me.user_type === 'client' && !me.is_staff && !me.is_superuser
  const isAdminPath = !!pathname && pathname.startsWith('/admin')
  const isInternalUser = !!me && !isClientUser

  const section = useMemo(() => {
    if (isClientUser) return 'dashboard'
    if (!pathname) return 'dashboard'
    if (pathname === '/') return 'dashboard'
    if (pathname.startsWith('/pentest')) return 'pentest'
    // Keep Pentest section active across related routes
    if (pathname.startsWith('/projects')) return 'pentest'
    if (pathname.startsWith('/scoping')) return 'pentest'
    if (pathname.startsWith('/sales')) return 'pentest'
    if (pathname.startsWith('/vmaas')) return 'vmaas'
    // Neutral routes should keep last active section, if any
    if (pathname.startsWith('/profile') || pathname.startsWith('/settings') || pathname.startsWith('/help')) {
      const last = persistedSection
      if (last === 'pentest' || last === 'vmaas' || last === 'sales') return last as any
    }
    return 'dashboard'
  }, [pathname, persistedSection, isClientUser])

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.body.dataset.section = section
    }
  }, [section])

  // Redirect clients away from internal-only routes (except healthcheck)
  const router = useRouter()
  useEffect(() => {
    if (!isClientUser || !pathname) return
    if (!isClientAllowedPath(pathname)) {
      router.replace(clientHomeRoute(!!me?.has_asm_portfolios))
    }
  }, [isClientUser, me?.has_asm_portfolios, pathname, router])

  useEffect(() => {
    if (!isClientUser || !isAdminPath) return
    router.replace(clientHomeRoute(!!me?.has_asm_portfolios))
  }, [isClientUser, isAdminPath, me?.has_asm_portfolios, router])

  // Ensure CSRF cookie exists early in the session for SessionAuthentication
  useEffect(() => {
    if (isMarketingPage || isAuthPage) return
    // Best-effort; errors ignored
    sessionCsrf().catch(() => {})
  }, [isAuthPage, isMarketingPage])

  // Persist last active section for neutral routes to reference
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (section === 'pentest' || section === 'vmaas' || section === 'sales') {
      try { window.localStorage.setItem('active.section', section) } catch {}
    }
  }, [section])

  useEffect(() => {
    if (typeof window === 'undefined') return
    const onceKey = 'chunkload.reloaded.once'
    const handler = (err: any) => {
      try {
        const reason = (err && (err.reason || err.error || err)) as any
        const msg = String(reason?.message || err?.message || '')
        const name = String(reason?.name || err?.name || '')
        const isChunkError = name.includes('ChunkLoadError') || msg.includes('Loading chunk') || msg.includes('CSS chunk load failed')
        if (!isChunkError) return
        const already = sessionStorage.getItem(onceKey)
        if (!already) {
          sessionStorage.setItem(onceKey, String(Date.now()))
          window.location.reload()
        }
      } catch {}
    }
    window.addEventListener('error', handler)
    window.addEventListener('unhandledrejection', handler as any)
    return () => {
      window.removeEventListener('error', handler)
      window.removeEventListener('unhandledrejection', handler as any)
    }
  }, [])
  if (isAdminPath && (isMeLoading || !isInternalUser)) return null

  return (
    <>
      {/* Always show top navbar on app routes; it adapts to auth state */}
      {!(isMarketingPage || isAuthPage || isTwoFASetup || noChrome) && <Navbar />}
      {!(isMarketingPage || isAuthPage || isTwoFASetup || noChrome) && <ClientSelectorDialog />}
      {isAuthed && !isMarketingPage && !isTwoFASetup && !noChrome && <RequireTwoFactor />}
      <AuthShell>{children}</AuthShell>
      <Toaster position="bottom-right" richColors />
    </>
  )
}
